import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { useTenantSettings, useUpdateTenantSettings } from './tenantQueries';

/** Die Fristen in der Reihenfolge, in der man sie durchgeht. */
const KEYS = [
  'closed_tickets_days',
  'satisfaction_surveys_days',
  'inactive_contacts_days',
  'attachments_days',
  'audit_log_days',
  'failed_logins_days',
] as const;

/**
 * Aufbewahrungsfristen je Mandant -- F5-13, K-152.
 *
 * **Sie standen bisher nur in der Datenbank.** Die Werte liegen seit Phase 1
 * in ``tenants.retention_policy``, aber es gab keinen Weg, sie zu setzen --
 * ein Löschkonzept, dessen Fristen nur per SQL zu pflegen sind, ist in einer
 * Prüfung schwer zu vertreten.
 *
 * **Der automatische Lauf ist ein eigener Schalter, und er ist aus.** Er
 * löscht endgültig, und wie viele Datensätze es sind, weiß vorher niemand.
 * Wer ihn einschaltet, hat den Probelauf im Reiter daneben gesehen.
 */
export function RetentionSettings() {
  const { t } = useTranslation();
  const settings = useTenantSettings();
  const update = useUpdateTenantSettings();

  const [draft, setDraft] = useState<Record<string, string>>({});

  const policy = settings.data?.retention_policy ?? {};
  const jobOn = settings.data?.features.retention_job === true;

  const valueOf = (key: string) => draft[key] ?? String(policy[key] ?? '');

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('retention.title')}</h2>
        <p className="table__muted">{t('retention.hint')}</p>

        {settings.isPending ? <LoadingIndicator /> : null}
        {settings.isError ? <ErrorNotice error={settings.error} /> : null}

        {settings.data
          ? KEYS.map((key) => (
              <div className="field" key={key}>
                <label className="field__label" htmlFor={`retention-${key}`}>
                  {t(`retentionKey.${key}`)}
                </label>
                <input
                  id={`retention-${key}`}
                  type="number"
                  min={1}
                  max={36500}
                  className="field__control"
                  value={valueOf(key)}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, [key]: event.target.value }));
                  }}
                />
              </div>
            ))
          : null}

        {update.isError ? <ErrorNotice error={update.error} /> : null}

        <div className="row">
          <Button
            variant="primary"
            disabled={Object.keys(draft).length === 0}
            busy={update.isPending}
            onClick={() => {
              const changed: Record<string, number> = {};
              for (const [key, value] of Object.entries(draft)) {
                const days = Number(value);
                if (Number.isFinite(days) && days > 0) changed[key] = days;
              }
              update.mutate(
                { retention_policy: changed },
                {
                  onSuccess: () => {
                    setDraft({});
                  },
                },
              );
            }}
          >
            {t('app.save')}
          </Button>
          {update.isSuccess ? (
            <span className="field__hint" role="status">
              {t('tickets.saved')}
            </span>
          ) : null}
        </div>
      </section>

      <section className="card">
        <h2>{t('retention.jobTitle')}</h2>
        <p className="table__muted">{t('retention.jobHint')}</p>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={jobOn}
            onChange={(event) => {
              update.mutate({ features: { retention_job: event.target.checked } });
            }}
          />
          {t('retention.jobToggle')}
        </label>
      </section>
    </div>
  );
}
